<script setup>
import {onMounted, onUnmounted, ref, watchEffect} from "vue";
import RequiredInformations from "@/components/shared/RequiredInformations.vue";
import QrcodeVue from "qrcode.vue";

const emit = defineEmits(["previous", "next", "goto"]);

const informations = ref([
  "Cosa Serve",
  "Indirizzo",
  "Aggiungi al tuo calendario",
]);
import {useStore} from "vuex";
import {
  deleteReservation,
  exportReservationDetail,
  getReservationDetailById,
  getResource,
  startPayment,
  translateDate,
  updateQueryParams,
} from "@/api/httpApi";

const store = useStore();

const resource = ref();
const reservationIdDetails = ref();
const isMobile = ref(false);
const modalRef = ref(null);
const qrUrl = ref(null);
let intervalId = null;

const handleResize = () => {
  isMobile.value = window.innerWidth < 768;
};

const isFiscalCodeRequired = (resource) =>
  resource?.requiredFields?.includes("FISCAL_CODE");
const isPhoneRequired = (resource) =>
  resource?.requiredFields?.includes("PHONE");
const isMobilePhoneRequired = (resource) =>
  resource?.requiredFields?.includes("MOBILE_PHONE");

const resetIfError = () => {
  store.commit("setStep", 0);
  store.commit("setSearched", false);
  store.commit("setResourceId", "");
  store.commit("setOfficeId", "");
  store.commit("setTopicId", "");
  store.commit("setSearchText", "");
  store.commit("setErogationMode", "fis");
  store.commit("setNumberOfReservations", 1);
  store.commit("setYear", undefined);
  store.commit("setMonth", undefined);
  store.commit("setDay", undefined);
  store.commit("setHour", undefined);
  store.commit("setSlots", []);
  store.commit("setReservationId", undefined);
  store.commit("setServiceId", undefined);
  store.commit("setDetail", "");
  store.commit("setAttributes", {});
  store.commit("setIdentifyValues", []);

  const elementToTop = document.getElementById("top-stepper");

  if (elementToTop) {
    setTimeout(() => {
      window.scrollTo({
        top: elementToTop.offsetTop - elementToTop.offsetHeight,
        left: 0,
        behavior: "smooth",
      });
    }, 500);
  }
  updateQueryParams([]);
  modalRef.value.show();
};

const getReservationUrl = () => {
  return window.location.href;
};

const onEdit = () => {
  updateQueryParams([]);
  store.commit("setIsEditing", true);
  emit("previous");
};

const onDelete = async () => {
  try {
    await deleteReservation(reservationIdDetails.value.reservationId);
    modalRef.value = new window.bootstrap.Modal(
      document.getElementById("deleteReservationIdModal")
    );
    resetIfError();
  } catch (error) {
    console.error(error);
  }
};

const onPrint = async () => {
  try {
    await exportReservationDetail(reservationIdDetails.value.reservationId);
  } catch (error) {
    console.error(error);
  }
};

const onPay = async () => {
  try {
    sessionStorage.removeItem("TOKEN_PAGO_PA");
    const redirectUrl = window.location.href.replace(
      "reservationId",
      "paymentReservationId"
    );
    const payload = {
      data: {
        redirectUrl: redirectUrl,
      },
    };
    const res = await startPayment(
      reservationIdDetails.value.reservationId,
      payload
    );

    if (res?.data?.redirectUrl) {
      const tokenIndex = res.data.redirectUrl.indexOf("token=");

      if (tokenIndex !== -1) {
        const tokenStartIndex = tokenIndex + "token=".length;
        const tokenEndIndex = res.data.redirectUrl.indexOf(
          "&",
          tokenStartIndex
        );
        const token =
          tokenEndIndex !== -1
            ? res.data.redirectUrl.substring(tokenStartIndex, tokenEndIndex)
            : res.data.redirectUrl.substring(tokenStartIndex);

        sessionStorage.setItem("TOKEN_PAGO_PA", token);
      }

      window.location.href = res.data.redirectUrl;
    }
  } catch (error) {
    console.error(error);
  }
};

watchEffect(async () => {
  const searchParams = new URLSearchParams(window.location.search);
  const searchReservationId = searchParams.get("reservationId");

  if (searchReservationId) {
    try {
      reservationIdDetails.value = await getReservationDetailById(
        searchReservationId
      );

      // Update the store
      store.commit("setResourceId", reservationIdDetails.value.resourceId);

      try {
        resource.value = await getResource(store.state.resourceId);
      } catch (err) {
        console.error(err);
        resource.value = undefined;
      }

      store.commit(
        "setNumberOfReservations",
        reservationIdDetails?.value?.reservationsNumber
      );
      store.commit("setSlots", reservationIdDetails?.value?.reservationSlots);
      store.commit(
        "setReservationId",
        reservationIdDetails?.value?.reservationId
      );
      store.commit(
        "setServiceId",
        reservationIdDetails?.value?.service?.serviceId
      );
      store.commit("setDetail", reservationIdDetails?.value?.detail);
      store.commit(
        "setAttributes",
        reservationIdDetails?.value?.attributesValues
      );
      store.commit(
        "setIdentifyValues",
        reservationIdDetails?.value?.itemsBooking
      );
      store.commit(
        "setErogationMode",
        reservationIdDetails?.value?.erogationMode
      );
      store.commit("patchUser", {
        firstName: reservationIdDetails?.value?.firstName,
        lastName: reservationIdDetails?.value?.lastName,
        email: reservationIdDetails?.value?.email,
        ...(isFiscalCodeRequired(resource.value)
          ? {fiscalCode: reservationIdDetails?.value?.fiscalCode}
          : {}),
        ...(isPhoneRequired(resource.value)
          ? {phone: reservationIdDetails?.value?.phone}
          : {}),
        ...(isMobilePhoneRequired(resource.value)
          ? {mobilePhone: reservationIdDetails?.value?.mobilePhone}
          : {}),
      });
      store.commit(
        "setYear",
        reservationIdDetails?.value?.reservationDate
          ? new Date(reservationIdDetails?.value?.reservationDate).getFullYear()
          : undefined
      );
      store.commit(
        "setMonth",
        reservationIdDetails?.value?.reservationDate
          ? new Date(reservationIdDetails?.value?.reservationDate).getMonth()
          : undefined
      );
      store.commit(
        "setDay",
        reservationIdDetails?.value?.reservationDate
          ? reservationIdDetails?.value?.reservationDate
          : undefined
      );
      store.commit("setServiceSlotNumber", reservationIdDetails?.value?.service?.slotsNumber);

      qrUrl.value = getReservationUrl();

    } catch (err) {
      console.error(err);
      reservationIdDetails.value = undefined;
      intervalId = setInterval(resetIfError, 1000);
      modalRef.value = new window.bootstrap.Modal(
        document.getElementById("errorReservationIdModal")
      );
      window.onpopstate = function () {
        location.reload();
      };
    }
  }
});

onMounted(() => {
  handleResize();
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

onUnmounted(() => {
  clearInterval(intervalId);
  window.onpopstate = null;
});
</script>

<template>
  <div class="container-fluid row mx-auto p-2">
    <div class="col-12 col-md-6 mb-5 px-0 px-md-3">

      <h2 class="h3 mb-3">{{ reservationIdDetails?.resourceName }}</h2>

      <p>
        L’appuntamento è fissato per
        <strong>
          {{ translateDate(reservationIdDetails?.reservationDate) }} dalle ore
          {{ reservationIdDetails?.reservationStartTime }} alle ore
          {{ reservationIdDetails?.reservationEndTime }}</strong
        >
      </p>
      <p
        v-if="
          reservationIdDetails?.paymentAmount &&
          reservationIdDetails?.paymentLastOutcome !== 'OK'
        "
      >
        Per confermare la prenotazione è necessario portare a termine il
        pagamento di
        <strong>{{
            (reservationIdDetails?.paymentAmount &&
              `${reservationIdDetails?.paymentAmount} €`) ||
            "-"
          }}</strong>
        <br/>entro la seguente data:
        <strong>{{ reservationIdDetails?.paymentExpiringDate || "-" }}</strong
        ><br/>Identificativo Univoco di Versamento:
        <strong>{{ reservationIdDetails?.iuvCode || "-" }}</strong>
      </p>
      <p>
        Codice prenotazione:
        <strong>{{ reservationIdDetails?.reservationId || "-" }}</strong>
        <br/><br/>

        <template v-if="reservationIdDetails?.erogationMode === 'vir'">
          <a :href="reservationIdDetails?.meetingUrl" target="_blank">
            Clicca qui per accedere alla riunione
          </a>
          <br/><br/>
        </template>

        Abbiamo inviato il riepilogo all’email:
        <strong>{{ reservationIdDetails?.email || "-" }}</strong>
      </p>
      <button
        type="button"
        class="mt-4 btn btn-outline-primary btn-lg d-flex align-items-center justify-content-center gap-2 col-12"
        @click="onPrint"
      >
        <svg
          class="icon icon-md align-self-start icon-primary icon-color"
          aria-hidden="true"
        >
          <use
            :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-download`"
          ></use>
        </svg>
        <span>Scarica la ricevuta (PDF 100KB)</span>
      </button>
    </div>
    <div
      class="col-12 col-md-6 mb-5 d-flex flex-column"
      :class="{ 'align-items-center': isMobile, 'align-items-end': !isMobile }"
    >
      <div class="d-flex gap-2 mb-4 mb-md-0">
        <button
          type="button"
          class="btn text-decoration-underline"
          @click="onEdit"
          v-if="
            resource?.reservationChangeable &&
            reservationIdDetails?.reservationStatus !== 'D'
          "
        >
          Modifica</button
        ><button
          type="button"
          class="btn text-decoration-underline"
          v-if="reservationIdDetails?.reservationStatus !== 'D'"
          @click="onDelete"
        >
          Elimina
        </button>
      </div>

      <qrcode-vue
        :value="getReservationUrl()"
        :size="isMobile ? 280 : 200"
        level="H"
        :margin="isMobile ? 0 : 5"
      />
    </div>

    <div class="col-3 border-end pe-0 d-none d-md-block">
      <RequiredInformations
        :informations="informations"
        :progress="
          !reservationIdDetails?.paymentLastOutcome ||
          reservationIdDetails?.paymentLastOutcome === 'OK'
            ? 100
            : 90
        "
      />
    </div>
    <div class="col-12 px-0 ps-md-5 pe-md-2 mb-5 pt-4 col-md-9">
      <div
        class="p-0 p-md-4 m-0 ms-md-5 bg-white mb-5"
        :class="{ 'shadow-sm': !isMobile }"
      >
        <div
          class="bg-light p-4 shadow-sm mb-5"
          :id="encodeURIComponent('Cosa Serve')"
          v-if="resource?.preWarning"
        >
          <h2 class="h3 mb-3">Indicazioni per l’appuntamento</h2>
          <div v-html="resource?.preWarning"></div>
        </div>
        <div
          class="bg-light p-4 shadow-sm mb-5"
          v-if="reservationIdDetails?.paymentAmount"
        >
          <h2 class="h3 mb-3">Indicazioni per il pagamento</h2>
          <div>
            Al seguente link verrai indirizzato alla pagina PagoPA per
            effettuare il pagamento di
            {{ reservationIdDetails?.paymentAmount }} €
          </div>
          <button
            type="button"
            class="mt-4 btn btn-primary btn-lg d-flex align-items-center justify-content-center gap-2 col-12 col-lg-6"
            @click="onPay"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 35 23"
              width="30px"
              fill="white"
            >
              <path
                class="st0"
                d="M24.4,6.9v2.9h-2.1V0h2.1h0.2h1.5C27.4,0,28,0.3,28,1v4.8c0,0.7-0.6,1-1.8,1L24.4,6.9L24.4,6.9L24.4,6.9zM26,1.1h-1.6v4.5H26V1.1z"
              />
              <path
                class="st0"
                d="M1.8,9.8v2.9H0V2.9h1.8H2h1.3c1.1,0,1.6,0.3,1.6,1v4.8c0,0.7-0.5,1-1.5,1L1.8,9.8L1.8,9.8z M3.1,4H1.8v4.5h1.3V4z"
              />
              <path
                class="st0"
                d="M10.4,9.7H7c-1,0-1.5-0.3-1.5-1v-2c0-0.7,0.5-1,1.5-1h1.6V4H7.3v0.9H5.5v-1c0-0.7,0.5-1,1.6-1h1.7c1,0,1.5,0.3,1.5,1L10.4,9.7L10.4,9.7z M7.3,8.6h1.3V6.8H7.3V8.6z"
              />
              <path
                class="st0"
                d="M14.2,9.7h-1.6c-1,0-1.5-0.3-1.5-1V3.9c0-0.7,0.5-1,1.6-1H14h0.2H16v8.8c0,0.7-0.5,1-1.6,1h-1.8c-1,0-1.6-0.3-1.6-1v-1h1.8v0.9h1.3L14.2,9.7L14.2,9.7z M12.9,8.6h1.3V4h-1.3V8.6z"
              />
              <path
                class="st0"
                d="M21.5,8.7c0,0.7-0.5,1-1.6,1h-1.7c-1,0-1.6-0.3-1.6-1V3.9c0-0.7,0.5-1,1.6-1H20c1,0,1.5,0.3,1.5,1V8.7L21.5,8.7z M19.8,4h-1.3v4.5h1.3V4z"
              />
              <path
                class="st0"
                d="M34.7,1.1c0-0.7-0.6-1-1.8-1h-2.1c-1.2,0-1.9,0.3-1.9,1v8.7H31v-3h1.6v2.5c0,6.4-5.2,11.6-11.6,11.6c-3.7,0-7-1.8-9.1-4.5l1.1-0.8l-4.3-2.9l0.2,6l1.4-1.1C12.7,20.9,16.6,23,21,23c7.6,0,13.7-6.1,13.7-13.7V1.1z M32.6,5.7H31V1.2h1.6V5.7z"
              />
            </svg>
            <span>Paga con PagoPA</span></button
          >
          <br/>
          <a href="https://www.pagopa.it" target="_blank">Che cos’è PagoPA</a>
        </div>
        <div class="bg-light p-4 shadow-sm mb-5" v-if="resource?.postWarning">
          <h2 class="h3 mb-3">Avvertenze post prenotazione</h2>
          <div v-html="resource?.postWarning"></div>
        </div>
        <div
          class="address-wrapper"
          :id="encodeURIComponent('Indirizzo')"
          v-if="resource?.place"
        >
          <h3 class="title-xxlarge mb-10">Indirizzo</h3>
          <div class="cmp-card-img row mx-auto">
            <div class="col-12 col-lg-6 px-0">
              <div class="card card-teaser card-teaser-info rounded shadow-sm">
                <div
                  class="card-body align-items-center card-body d-flex justify-content-between"
                >
                  <div class="left">
                    <h6>{{ resource?.place }}</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="cmp-icon-list mx-0 mx-md-5"
        :id="encodeURIComponent('Aggiungi al tuo calendario')"
      >
        <h2 class="h3 mt-40 mb-2 mb-lg-4">
          Aggiungi al tuo calendario
        </h2>
        <div class="link-list-wrapper">
          <ul class="link-list mt-3">
            <li class="">
              <a
                class="list-item icon-left t-primary title-small-semi-bold"
                href="https://outlook.live.com/calendar/0/deeplink/compose?subject=Prenotazione%20Appuntamento&body=L'appuntamento%20è%20fissato%20per%20{{ translateDate(reservationIdDetails?.reservationDate) }}%20dalle%20ore%20{{ reservationIdDetails?.reservationStartTime }}%20alle%20ore%20{{ reservationIdDetails?.reservationEndTime }}"
                target="_blank"
              >
                <span class="list-item-title-icon-wrapper">
                  <svg
                    class="icon icon-sm align-self-start icon-primary icon-color"
                    aria-hidden="true"
                  >
                    <use
                      :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-calendar`"
                    ></use>
                  </svg>
                  <span class="list-item-title title-small-semi-bold fw-normal"
                  >Outlook</span
                  >
                </span>
              </a>
            </li>
            <li class="">
              <a
                class="list-item icon-left t-primary title-small-semi-bold"
                href="https://calendar.google.com/calendar/r/eventedit?text=Prenotazione%20Appuntamento&details=L'appuntamento%20è%20fissato%20per%20{{ translateDate(reservationIdDetails?.reservationDate) }}%20dalle%20ore%20{{ reservationIdDetails?.reservationStartTime }}%20alle%20ore%20{{ reservationIdDetails?.reservationEndTime }}"
                target="_blank"
              >
                <span class="list-item-title-icon-wrapper">
                  <svg
                    class="icon icon-sm align-self-start icon-primary icon-color"
                    aria-hidden="true"
                  >
                    <use
                      :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-calendar`"
                    ></use>
                  </svg>
                  <span class="list-item-title title-small-semi-bold fw-normal"
                  >Google Calendar</span
                  >
                </span>
              </a>
            </li>
            <li class="">
              <a
                class="list-item icon-left t-primary title-small-semi-bold"
                href="https://calendar.icloud.com/"
                target="_blank"
              >
                <span class="list-item-title-icon-wrapper">
                  <svg
                    class="icon icon-sm align-self-start icon-primary icon-color"
                    aria-hidden="true"
                  >
                    <use
                      :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-calendar`"
                    ></use>
                  </svg>
                  <span class="list-item-title title-small-semi-bold fw-normal"
                  >iCloud Calendar</span
                  >
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal fade"
    tabindex="-1"
    role="dialog"
    id="errorReservationIdModal"
    aria-labelledby="errorReservationIdModalTitle"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title h5" id="errorReservationIdModalTitle">
            Id non trovato!
          </h2>
        </div>
        <div class="modal-footer">
          <button
            class="btn btn-primary btn-sm"
            data-bs-dismiss="modal"
            type="button"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal fade"
    tabindex="-1"
    role="dialog"
    id="deleteReservationIdModal"
    aria-labelledby="deleteReservationIdModalTitle"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title h5" id="deleteReservationIdModalTitle">
            Prenotazione cancellata!
          </h2>
        </div>
        <div class="modal-footer">
          <button
            class="btn btn-primary btn-sm"
            data-bs-dismiss="modal"
            type="button"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
